import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { PatientContext } from 'contexts/PatientContext'
import cn from 'classnames';
import './index.css'

export const Stepper = () => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { patientId } = useParams();
	const [currentStep, setCurrentStep] = useState(1);
	const { patient } = useContext<any>(PatientContext);

	const getCurrentStep = () => {
		if(isCurrentPath('/new-patient/demography', `/edit-patient/${patientId}/demography`)){
			return 1;
		}
		if(isCurrentPath('/new-patient/samples', `/edit-patient/${patientId}/samples`)){
			return 2;
		}
		if(isCurrentPath('/new-patient/diagnostic', `/edit-patient/${patientId}/diagnostic`)){
			return 3;
		}
		if(isCurrentPath('/new-patient/treatments', `/edit-patient/${patientId}/treatments`)){
			return 4;
		}
		if(isCurrentPath('/new-patient/lab-report', `/edit-patient/${patientId}/lab-report`)){
			return 5;
		}
		if(isCurrentPath('/new-patient/final', `/edit-patient/${patientId}/final`)){
			return 6;
		}
		return 1;
	}

	useEffect(() => {
		setCurrentStep(getCurrentStep());
	}, [pathname])

	const isCurrentPath = (path: string, path2?: string) => {
		return (pathname === path || pathname === path2);
	};

	return (
		<div className='flex'>
			<div>
				<div className={cn()}></div>
			</div>
			<div className='flex flex-col'>
				<Link
					to={patientId ? `/edit-patient/${patientId}/demography` : '/new-patient/demography'}
					className={cn('p-2 text-grey-dark text-sm opacity-50', {
						'text-lg opacity-100': currentStep === 1,
					})}
				>
					<div className="flex items-center">
						<div className={cn("current-step absolute block flex items-center justify-center rounded-full bg-orange",
							{
								'w-6 h-6 -ml-1.5': currentStep <= 1,
								'visited w-3 h-3': currentStep > 1,
								'is-current': currentStep === 1

							}
						)}>
							{currentStep === 1 && 
								<span className="step-number text-xs text-white font-bold p-2">1</span>
							}
						</div>
						<span className="ml-12">
							{t('demography')}
						</span>
					</div>
				</Link>
				<Link
					to={patientId ? `/edit-patient/${patientId}/samples` : '/new-patient/samples'}
					className={cn('p-2 text-grey-dark text-sm opacity-50', {
						'text-lg opacity-100': currentStep === 2,
						'disabled': !patientId && !patient?.id
					})}
				>
					<div className="flex items-center">
						<div className={cn("current-step absolute flex items-center justify-center rounded-full bg-orange",
							{
								'w-6 h-6 -ml-1.5': currentStep <= 2,
								'visited w-3 h-3': currentStep > 2,
								'is-current': currentStep === 2

							}
						)}>
							{currentStep === 2 && 
								<span className="step-number text-xs text-white font-bold p-2">
									2
								</span>
							}
						</div>
						<span className="ml-12">
							{t('preTreatmentSamples')}
						</span>
					</div>
				</Link>
				<Link
					to={patientId ? `/edit-patient/${patientId}/diagnostic` : '/new-patient/diagnostic'}
					className={cn('p-2 text-grey-dark text-sm opacity-50', {
						'text-lg opacity-100': currentStep === 3,
						'disabled': !patientId && !patient?.id
					})}
				>
					<div className="flex items-center">
						<div className={cn("current-step absolute flex items-center justify-center rounded-full bg-orange",
							{
								'w-6 h-6 -ml-1.5': currentStep <= 3,
								'visited w-3 h-3': currentStep > 3,
								'is-current': currentStep === 3

							}
						)}>
							{currentStep === 3 && 
								<span className="step-number text-xs text-white font-bold p-2">
									3
								</span>
							}
						</div>
						<span className="ml-12">
							{t('diagnostic')}
						</span>
					</div>
				</Link>
				<Link
					to={patientId ? `/edit-patient/${patientId}/treatments` : '/new-patient/treatments'}
					className={cn('p-2 text-grey-dark text-sm opacity-50', {
						'text-lg opacity-100': currentStep === 4,
						'disabled': !patientId && !patient?.id
					})}
				>
					<div className="flex items-center">
						<div className={cn("current-step absolute flex items-center justify-center rounded-full bg-orange",
							{
								'w-6 h-6 -ml-1.5': currentStep <= 4,
								'visited w-3 h-3': currentStep > 4,
								'is-current': currentStep === 4

							}
						)}>
							{currentStep === 4 && 
								<span className="step-number text-xs text-white font-bold p-2">
									4
								</span>
							}
						</div>
						<span className="ml-12">
							{t('treatments')}
						</span>
					</div>
				</Link>
				<Link
					to={patientId ? `/edit-patient/${patientId}/lab-report` : '/new-patient/lab-report'}
					className={cn('p-2 text-grey-dark text-sm opacity-50', {
						'text-lg opacity-100': currentStep === 5,
						'disabled': !patientId && !patient?.id
					})}
				>
					<div className="flex items-center">
						<div className={cn("current-step absolute flex items-center justify-center rounded-full bg-orange",
							{
								'w-6 h-6 -ml-1.5': currentStep <= 5,
								'visited w-3 h-3': currentStep > 5,
								'is-current': currentStep === 5

							}
						)}>
							{currentStep === 5 && 
								<span className="step-number text-xs text-white font-bold p-2">
									5
								</span>
							}
						</div>
						<span className="ml-12">
							{t('laboratoryReport')}
						</span>
					</div>
				</Link>
				<Link
					to={patientId ? `/edit-patient/${patientId}/final` : '/new-patient/final'}
					className={cn('p-2 text-grey-dark text-sm opacity-50', {
						'text-lg opacity-100': currentStep === 6,
						'disabled': !patientId && !patient?.id
					})}
				>
					<div className="flex items-center">
						<div className={cn("current-step final-step absolute flex items-center justify-center rounded-full bg-orange",
							{
								'w-6 h-6 -ml-1.5': currentStep <= 6,
								'visited w-3 h-3': currentStep > 6,
								'is-current': currentStep === 6

							}
						)}>
							{currentStep === 6 && 
								<span className="step-number text-xs text-white font-bold p-2">
									6
								</span>
							}
						</div>
						<span className="ml-12">
							{t('final')}
						</span>
					</div>
				</Link>
			</div>
		</div>
	);
};
